import { X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @param {{
 *   open: boolean,
 *   meetingTitle?: string,
 *   submitting?: boolean,
 *   submitError?: string | null,
 *   onClose: () => void,
 *   onSubmit: (guest: {
 *     firstName: string,
 *     lastName: string,
 *     phoneNumber: string,
 *   }) => void | Promise<void>,
 * }} props
 */
export function MeetingGuestAddModal({
    open,
    meetingTitle = '',
    submitting = false,
    submitError = null,
    onClose,
    onSubmit,
}) {
    const { t } = useTranslation();
    const titleId = useId();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [formError, setFormError] = useState(
        /** @type {string | null} */ (null),
    );

    useEffect(() => {
        if (!open) {
            setFirstName('');
            setLastName('');
            setPhoneNumber('');
            setFormError(null);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;

        function onKeyDown(e) {
            if (e.key === 'Escape' && !submitting) {
                onClose();
            }
        }

        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, onClose, submitting]);

    if (!open) return null;

    async function handleSubmit(e) {
        e.preventDefault();
        setFormError(null);

        const guest = {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            phoneNumber: phoneNumber.trim(),
        };

        if (!guest.firstName || !guest.lastName || !guest.phoneNumber) {
            setFormError(t('eventCalendarPage.guestFormValidationError'));
            return;
        }

        try {
            await onSubmit(guest);
        } catch {
            // submitError parent'tan gelir
        }
    }

    const displayError = formError || submitError;

    return (
        <div
            className="app-modal-backdrop app-modal-backdrop--stacked"
            onClick={submitting ? undefined : onClose}
            role="presentation"
        >
            <div
                className="app-modal app-modal--wide event-calendar-guest-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="app-modal-header">
                    <div className="min-w-0">
                        <h2 id={titleId} className="app-modal-title h6 mb-0">
                            {t('eventCalendarPage.addGuestModalTitle')}
                        </h2>
                        {meetingTitle ? (
                            <p className="event-calendar-guest-modal-meeting small text-secondary mb-0 mt-1">
                                {meetingTitle}
                            </p>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        className="app-modal-close btn btn-link p-1"
                        onClick={onClose}
                        disabled={submitting}
                        aria-label={t('eventCalendarPage.detailClose')}
                    >
                        <X size={20} strokeWidth={2} aria-hidden />
                    </button>
                </div>

                <form className="app-modal-body app-modal-body--scroll" onSubmit={handleSubmit}>
                    <p className="event-calendar-guest-modal-intro small text-secondary mb-3">
                        {t('eventCalendarPage.detailGuestFormIntro')}
                    </p>
                    <div className="event-calendar-guest-modal-fields">
                        <div className="mb-3">
                            <label
                                htmlFor={`${titleId}-first-name`}
                                className="form-label event-calendar-detail-field-label"
                            >
                                {t('eventCalendarPage.detailGuestFirstName')}
                            </label>
                            <input
                                id={`${titleId}-first-name`}
                                type="text"
                                className="form-control"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                autoComplete="given-name"
                                disabled={submitting}
                            />
                        </div>
                        <div className="mb-3">
                            <label
                                htmlFor={`${titleId}-last-name`}
                                className="form-label event-calendar-detail-field-label"
                            >
                                {t('eventCalendarPage.detailGuestLastName')}
                            </label>
                            <input
                                id={`${titleId}-last-name`}
                                type="text"
                                className="form-control"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                autoComplete="family-name"
                                disabled={submitting}
                            />
                        </div>
                        <div className="mb-0">
                            <label
                                htmlFor={`${titleId}-phone`}
                                className="form-label event-calendar-detail-field-label"
                            >
                                {t('eventCalendarPage.detailGuestPhone')}
                            </label>
                            <input
                                id={`${titleId}-phone`}
                                type="tel"
                                className="form-control"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                autoComplete="tel"
                                disabled={submitting}
                            />
                        </div>
                    </div>

                    {displayError ? (
                        <p
                            className="app-modal-error small text-danger mb-0 mt-3"
                            role="alert"
                        >
                            {displayError}
                        </p>
                    ) : null}

                    <button
                        type="submit"
                        className="btn btn-primary w-100 event-calendar-detail-join-btn mt-3"
                        disabled={submitting}
                    >
                        {submitting
                            ? t('eventCalendarPage.addGuestSubmitting')
                            : t('eventCalendarPage.addGuest')}
                    </button>
                </form>
            </div>
        </div>
    );
}
